import { ElementRef } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { ScrollableDirective } from '../scroll/scrollable.directive';
import {
  ConnectedOverlayPositionChange,
  ConnectionPositionPair,
  OriginConnectionPosition,
  OverlayConnectionPosition,
  ScrollableViewProperties,
} from './connected-position';
import { PositionStrategy } from './position-strategy';
import { ViewportRuler } from './viewport-ruler';

/**
 * Container to hold the bounding positions of a particular element with respect to the viewport,
 * where top and bottom are the y-axis coordinates of the bounding rectangle and left and right are
 * the x-axis coordinates.
 */
export type ElementBoundingPositions = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

type Direction = 'rtl' | 'ltr';

/**
 * A strategy for positioning overlays. Using this strategy, an overlay is given an
 * implicit position relative some origin element. The relative position is defined in terms of
 * a point on the origin element that is connected to a point on the overlay element. For example,
 * a basic dropdown is connecting the bottom-left corner of the origin to the top-left corner
 * of the overlay.
 */
export class ConnectedPositionStrategy implements PositionStrategy {
  /** Ordered list of preferred positions, from most to least desirable. */
  public _preferredPositions: ConnectionPositionPair[] = [];

  public _onPositionChange: Subject<ConnectedOverlayPositionChange> =
    new Subject<ConnectedOverlayPositionChange>();

  /** Whether the we're dealing with an RTL context */
  public get _isRtl(): boolean {
    return this._dir === 'rtl';
  }

  /** Emits an event when the connection point changes. */
  public get onPositionChange(): Observable<ConnectedOverlayPositionChange> {
    return this._onPositionChange.asObservable();
  }

  /** Ordered list of preferred positions, from most to least desirable. */
  public get positions(): ConnectionPositionPair[] {
    return this._preferredPositions;
  }

  private _dir: Direction = 'ltr';

  /** The offset in pixels for the overlay connection point on the x-axis */
  private _offsetX: number = 0;

  /** The offset in pixels for the overlay connection point on the y-axis */
  private _offsetY: number = 0;

  /** The Scrollable containers used to check scrollable view properties on position change. */
  private scrollables: ScrollableDirective[] = [];

  /** The origin element against which the overlay will be positioned. */
  private _origin: HTMLElement;

  constructor(
    private _connectedTo: ElementRef,
    private _originPos: OriginConnectionPosition,
    private _overlayPos: OverlayConnectionPosition,
    private _viewportRuler: ViewportRuler,
  ) {
    this._origin = this._connectedTo.nativeElement;
    this.withFallbackPosition(_originPos, _overlayPos);
  }

  /**
   * To be used to for any cleanup after the element gets destroyed.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public dispose(): void {}

  /**
   * Updates the position of the overlay element, using whichever preferred position relative
   * to the origin fits on-screen.
   * @docs-private
   *
   * @param element Element to which to apply the CSS styles.
   * @returns Resolves when the styles have been applied.
   */
  public apply(element: HTMLElement): Promise<void | null> {
    // We need the bounding rects for the origin and the overlay to determine how to position
    // the overlay relative to the origin.
    const originRect = this._origin.getBoundingClientRect();
    const overlayRect = element.getBoundingClientRect();

    // We use the viewport rect to determine whether a position would go off-screen.
    const viewportRect = this._viewportRuler.getViewportRect();

    // Fallback point if none of the fallbacks fit into the viewport.
    let fallbackPoint: OverlayPoint | null = null;

    // We want to place the overlay in the first of the preferred positions such that the
    // overlay fits on-screen.
    for (const pos of this._preferredPositions) {
      // Get the (x, y) point of connection on the origin, and then use that to get the
      // (top, left) coordinate for the overlay at `pos`.
      const originPoint = this._getOriginConnectionPoint(originRect, pos);
      const overlayPoint = this._getOverlayPoint(originPoint, overlayRect, viewportRect, pos);

      // If the overlay in the calculated position fits on-screen, put it there and we're done.
      if (overlayPoint.fitsInViewport) {
        this._setElementPosition(element, overlayPoint);

        // Notify that the position has been changed along with its change properties.
        const scrollableViewProperties = this.getScrollableViewProperties(element);
        const positionChange = new ConnectedOverlayPositionChange(pos, scrollableViewProperties);
        this._onPositionChange.next(positionChange);

        return Promise.resolve(null);
      } else if (!fallbackPoint || fallbackPoint.visibleArea! < overlayPoint.visibleArea!) {
        fallbackPoint = overlayPoint;
      }
    }

    // If none of the preferred positions were in the viewport, take the one
    // with the largest visible area.
    this._setElementPosition(element, fallbackPoint!);

    return Promise.resolve(null);
  }

  public findPosition<T extends ConnectionPositionPair>(
    element: HTMLElement,
    positions: T[],
  ): T | void {
    const originRect = this._origin.getBoundingClientRect();
    const overlayRect = element.getBoundingClientRect();
    const viewportRect = this._viewportRuler.getViewportRect();

    for (const pos of positions) {
      const originPoint = this._getOriginConnectionPoint(originRect, pos);
      const overlayPoint = this._getOverlayPoint(originPoint, overlayRect, viewportRect, pos);

      if (overlayPoint.fitsInViewport) {
        return pos;
      }
    }
  }

  /**
   * Sets the list of Scrollable containers that host the origin element so that
   * on reposition we can evaluate if it or the overlay has been clipped or outside view. Every
   * Scrollable must be an ancestor element of the strategy's origin element.
   */
  public withScrollableContainers(scrollables: ScrollableDirective[]): void {
    this.scrollables = scrollables;
  }

  /**
   * Adds a new preferred fallback position.
   * @param originPos
   * @param overlayPos
   */
  public withFallbackPosition(
    originPos: OriginConnectionPosition,
    overlayPos: OverlayConnectionPosition,
  ): this {
    this._preferredPositions.push(new ConnectionPositionPair(originPos, overlayPos));
    return this;
  }

  /**
   * Sets the layout direction so the overlay's position can be adjusted to match.
   * @param dir New layout direction.
   */
  public withDirection(dir: 'ltr' | 'rtl'): this {
    this._dir = dir;
    return this;
  }

  /**
   * Sets an offset for the overlay's connection point on the x-axis
   * @param offset New offset in the X axis.
   */
  public withOffsetX(offset: number): this {
    this._offsetX = offset;
    return this;
  }

  /**
   * Sets an offset for the overlay's connection point on the y-axis
   * @param  offset New offset in the Y axis.
   */
  public withOffsetY(offset: number): this {
    this._offsetY = offset;
    return this;
  }

  /**
   * Gets the horizontal (x) "start" dimension based on whether the overlay is in an RTL context.
   * @param rect
   */
  private _getStartX(rect: ClientRect): number {
    return this._isRtl ? rect.right : rect.left;
  }

  /**
   * Gets the horizontal (x) "end" dimension based on whether the overlay is in an RTL context.
   * @param rect
   */
  private _getEndX(rect: ClientRect): number {
    return this._isRtl ? rect.left : rect.right;
  }

  /**
   * Gets the (x, y) coordinate of a connection point on the origin based on a relative position.
   * @param originRect
   * @param pos
   */
  private _getOriginConnectionPoint(originRect: ClientRect, pos: ConnectionPositionPair): Point {
    const originStartX = this._getStartX(originRect);
    const originEndX = this._getEndX(originRect);

    let x: number;
    if (pos.originX === 'center') {
      x = originStartX + originRect.width / 2;
    } else {
      x = pos.originX === 'start' ? originStartX : originEndX;
    }

    let y: number;
    if (pos.originY === 'center') {
      y = originRect.top + originRect.height / 2;
    } else {
      y = pos.originY === 'top' ? originRect.top : originRect.bottom;
    }

    return { x, y };
  }

  /**
   * Gets the (x, y) coordinate of the top-left corner of the overlay given a given position and
   * origin point to which the overlay should be connected, as well as how much of the element
   * would be inside the viewport at that position.
   */
  private _getOverlayPoint(
    originPoint: Point,
    overlayRect: ClientRect,
    viewportRect: ClientRect,
    pos: ConnectionPositionPair,
  ): OverlayPoint {
    // Calculate the (overlayStartX, overlayStartY), the start of the potential overlay position
    // relative to the origin point.
    let overlayStartX: number;
    if (pos.overlayX === 'center') {
      overlayStartX = -overlayRect.width / 2;
    } else if (pos.overlayX === 'start') {
      overlayStartX = this._isRtl ? -overlayRect.width : 0;
    } else {
      overlayStartX = this._isRtl ? 0 : -overlayRect.width;
    }

    let overlayStartY: number;
    if (pos.overlayY === 'center') {
      overlayStartY = -overlayRect.height / 2;
    } else {
      overlayStartY = pos.overlayY === 'top' ? 0 : -overlayRect.height;
    }

    // The (x, y) coordinates of the overlay.
    const x = originPoint.x + overlayStartX + this._offsetX;
    const y = originPoint.y + overlayStartY + this._offsetY;

    // How much the overlay would overflow at this position, on each side.
    const leftOverflow = viewportRect.left - x;
    const rightOverflow = x + overlayRect.width - viewportRect.right;
    const topOverflow = -y;
    const bottomOverflow = y + overlayRect.height - viewportRect.height;

    // Visible parts of the element on each axis.
    const visibleWidth = this._subtractOverflows(overlayRect.width, leftOverflow, rightOverflow);
    const visibleHeight = this._subtractOverflows(overlayRect.height, topOverflow, bottomOverflow);

    // The area of the element that's within the viewport.
    const visibleArea = visibleWidth * visibleHeight;
    const fitsInViewport = overlayRect.width * overlayRect.height === visibleArea;

    return { x, y, fitsInViewport, visibleArea };
  }

  /**
   * Gets the view properties of the trigger and overlay, including whether they are clipped
   * or completely outside the view of any of the strategy's scrollables.
   */
  private getScrollableViewProperties(overlay: HTMLElement): ScrollableViewProperties {
    const originBounds = this._getElementBounds(this._origin);
    const overlayBounds = this._getElementBounds(overlay);
    const scrollContainerBounds = this.scrollables.map((scrollable: ScrollableDirective) =>
      this._getElementBounds(scrollable.getElementRef().nativeElement),
    );

    return {
      isOriginClipped: this.isElementClipped(originBounds, scrollContainerBounds),
      isOriginOutsideView: this.isElementOutsideView(originBounds, scrollContainerBounds),
      isOverlayClipped: this.isElementClipped(overlayBounds, scrollContainerBounds),
      isOverlayOutsideView: this.isElementOutsideView(overlayBounds, scrollContainerBounds),
    };
  }

  /** Whether the element is completely out of the view of any of the containers. */
  private isElementOutsideView(
    elementBounds: ElementBoundingPositions,
    containersBounds: ElementBoundingPositions[],
  ): boolean {
    return containersBounds.some((containerBounds: ElementBoundingPositions) => {
      const outsideAbove = elementBounds.bottom < containerBounds.top;
      const outsideBelow = elementBounds.top > containerBounds.bottom;
      const outsideLeft = elementBounds.right < containerBounds.left;
      const outsideRight = elementBounds.left > containerBounds.right;

      return outsideAbove || outsideBelow || outsideLeft || outsideRight;
    });
  }

  /** Whether the element is clipped by any of the containers. */
  private isElementClipped(
    elementBounds: ElementBoundingPositions,
    containersBounds: ElementBoundingPositions[],
  ): boolean {
    return containersBounds.some((containerBounds: ElementBoundingPositions) => {
      const clippedAbove = elementBounds.top < containerBounds.top;
      const clippedBelow = elementBounds.bottom > containerBounds.bottom;
      const clippedLeft = elementBounds.left < containerBounds.left;
      const clippedRight = elementBounds.right > containerBounds.right;

      return clippedAbove || clippedBelow || clippedLeft || clippedRight;
    });
  }

  /**
   * Physically positions the overlay element to the given coordinate.
   * @param element
   * @param overlayPoint
   */
  private _setElementPosition(element: HTMLElement, overlayPoint: Point): void {
    element.style.left = overlayPoint.x + 'px';
    element.style.top = overlayPoint.y + 'px';
  }

  /** Returns the bounding positions of the provided element with respect to the viewport. */
  private _getElementBounds(element: HTMLElement): ElementBoundingPositions {
    const boundingClientRect = element.getBoundingClientRect();
    return {
      top: boundingClientRect.top,
      right: boundingClientRect.left + boundingClientRect.width,
      bottom: boundingClientRect.top + boundingClientRect.height,
      left: boundingClientRect.left,
    };
  }

  /**
   * Subtracts the amount that an element is overflowing on an axis from it's length.
   */
  private _subtractOverflows(length: number, ...overflows: number[]): number {
    return overflows.reduce(
      (currentValue: number, currentOverflow: number) =>
        currentValue - Math.max(currentOverflow, 0),
      length,
    );
  }
}

/** A simple (x, y) coordinate. */
interface Point {
  x: number;
  y: number;
}

/**
 * Expands the simple (x, y) coordinate by adding info about whether the
 * element would fit inside the viewport at that position, as well as
 * how much of the element would be visible.
 */
interface OverlayPoint extends Point {
  visibleArea?: number;
  fitsInViewport?: boolean;
}
